import { Flex,Table, Typography } from "antd";
import { debounce } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent.jsx";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { useHandlePagination } from "~hooks/useHandlePagination.js";
import { useSportConfiguration } from "~hooks/useSportConfiguration.js";
import { Genders, Statuses } from "~utilities/constants.js";
import TeamListFilter from "./TeamListFilter";
import useFetchEntities from "~pages/Entities/EntitiesListPage/hooks/useFetchEntites.ts";

const statusFilter = [
  {
    text: "Active Teams",
    value: Statuses.ACTIVE,
  },
  {
    text: "Inactive Teams",
    value: Statuses.INACTIVE,
  },
  {
    text: "Pending Teams",
    value: Statuses.PENDING,
  },
];

const GenderFilter = [
  {
    text: Genders.FEMALE,
    value: Genders.FEMALE,
  },
  {
    text: Genders.MALE,
    value: Genders.MALE,
  },
  {
    text: Genders.MIXED,
    value: Genders.MIXED,
  },
  {
    text: Genders.UNKNOWN,
    value: Genders.UNKNOWN,
  },
];

const TeamsList = ({ columns = null, defaultPageSize }) => {
  const { formatMessage } = useIntl();
  const { state } = useLocation();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(defaultPageSize || 1000);
  const { entityStandards, ageGroups, disciplines } = useSportConfiguration();
  const [ searchText, setSearchText ] = useState("");
  const [ activeFilter, setActiveFilter ] = useState({
    status: state?.status || "ACTIVE",
    standard: "ALL",
    gender: "ALL",
    ageGroup: "ALL",
    discipline: "ALL",
  });



  const { data, isLoading } = useFetchEntities({
    limit: pageSize,
    offset: offset,
    nameFullLocalContains: searchText?.length > 1 ? searchText : "",
    status: activeFilter?.status !== "ALL" ? activeFilter.status : "",
    standard: activeFilter?.standard !== "ALL" ? activeFilter.standard : "",
    gender: activeFilter?.gender !== "ALL" ? activeFilter.gender : "",
    ageGroup: activeFilter?.ageGroup !== "ALL" ? activeFilter.ageGroup : "",
    discipline: activeFilter?.discipline !== "ALL" ? activeFilter.discipline : "",
  });

  const StandardsFilter = useMemo(() => {
    return [ ...entityStandards.map((item) => ({ text: item, value: item })) ];
  }, [ entityStandards ]);

  const AgeGroupFilter = useMemo(() => {
    return [ ...ageGroups.map((item) => ({ text: item, value: item })) ];
  }, [ ageGroups ]);

  const DisciplineFilter = useMemo(() => {
    return [ ...disciplines.map((item) => ({ text: item, value: item })) ];
  }, [ disciplines ]);

  const teams = data?.data || [];
  const nextLink = data?.hasNextLink;

  const updateSearch = useMemo(() => debounce((searchText) => setSearchText(searchText), 500), [ setSearchText ]);

  const handleChangeFilter = useCallback((key, value) => {
    setActiveFilter((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <Typography.Title level={4}>Teams</Typography.Title>
      </Flex>
      <Flex align="center" gap={8}>
        <TeamListFilter
          title={formatMessage({ id: "standard" })}
          value={activeFilter?.standard}
          handleChange={(value) => handleChangeFilter("standard", value)}
          options={[ { text: "ALL", value: "ALL" }, ...StandardsFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "gender" })}
          value={activeFilter?.gender}
          handleChange={(value) => handleChangeFilter("gender", value)}
          options={[ { text: "ALL", value: "ALL" }, ...GenderFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "ageGroup" })}
          value={activeFilter?.ageGroup}
          handleChange={(value) => handleChangeFilter("ageGroup", value)}
          options={[ { text: "ALL", value: "ALL" }, ...AgeGroupFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "discipline" })}
          value={activeFilter?.discipline}
          handleChange={(value) => handleChangeFilter("discipline", value)}
          options={[ { text: "ALL", value: "ALL" }, ...DisciplineFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "status" })}
          value={activeFilter?.status}
          handleChange={(value) => handleChangeFilter("status", value)}
          options={[ { text: "ALL", value: "ALL" }, ...statusFilter ]}
        />
        <SearchFilter
          searchValue={searchText}
          doSearch={updateSearch}
          placeholder={formatMessage({
            id: "person.search.placeholder.2",
            defaultMessage: "Search (2 letters min)",
          })}
        />
      </Flex>
      <Table
        loading={isLoading}
        size={"small"}
        columns={columns}
        rowKey={"entityId"}
        dataSource={teams}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + teams.length + 1 : teams.length + offset,
          position: [ "bottomCenter" ],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
      />
    </Flex>
  );
};

export default TeamsList;
